import React from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import fetchDocumentToViewer from 'store/reducers/viewerCreator';
import { useTranslation } from 'react-i18next';
import WebViewer from 'components/Webviewer/WebViewer';
import {
  setDocsSegment, setIsOpenDeleteEntityModal, setIsOpenRenameEntityModal,
  setIsOpenShareEntityModal, setIsRSidebarCollapsed, setIsUploadingSomeChat
} from 'store/slices/windowStateSlice';
import { setIsOpenAddNoteModal, setNotes, setSelectedNote } from 'store/slices/dataNotes';
import fetchGroupsAndUsers from 'store/reducers/usersGroupCreator';
import { setEntityOfContextMenu, setSelectedDocument, setSelectedVersion, setViewType } from 'store/slices/dataDocumentsSlice';
import { fetchAllNotes } from 'store/reducers/notesCreator';
import LogsTable from 'components/LogsTable/LogsTable';
import { fetchAvailableDocumentsLogs } from 'store/reducers/documentsLogsCreator';
import { setDocumentsLogs } from 'store/slices/dataLogsSlice';
import { resetViewerSlice, setViewerAbortController } from 'store/slices/viewerSlice';
import api from 'api';
import dayjs from 'dayjs';
import { IChat, IDocument } from 'interfaces';
import { resetAgreementState } from 'store/slices/dataAgreementsSlice';
import { resetSigningState } from 'store/slices/signingSlice';
import NotesTab from './NotesTab/NotesTab';
import VersionsTab from './VersionsTab/VersionsTab';
import AddNoteModal from './NotesTab/AddNoteModal';
import { createChat, fetchChatHistory, fetchSetSelectedChat } from 'store/reducers/chatCreator';
import Chat from 'routes/Chat/Chat';
import { ConfirmationModal, RenameModal } from 'components/Modals';
import { useNavigate, useParams } from 'react-router';
import { deleteTargetEntity } from 'store/reducers/documentsCreator';
import ShareModal from 'components/Modals/ShareModal/ShareModal';
import EditTab from './EditTab/EditTab';
import { setMessageForReply } from 'store/slices/chatsSlice';
import { convertableTypesFiles } from 'helpers/filesExtensions';
import TranscriptionTab from './TranscriptionTab/TranscriptionTab';
import { checkTranscription } from 'store/reducers/aiFunctionsCreator';
import { setIsOpenAIAssistanceSider, setIsOpenAnalysisSider, setSessionStatus, setTranscription, setTranscriptionText } from 'store/slices/aiFunctionsDataSlice';
import { documnentIcons, getCorrectIcon } from 'helpers/documentIcons';

const isAvailableEditing = window.REACT_APP_IS_EDITING_ACTIVE !== 'REPLACE_REACT_APP_IS_EDITING_ACTIVE' ? window.REACT_APP_IS_EDITING_ACTIVE : process.env.REACT_APP_IS_EDITING_ACTIVE || 'false';

export const sectionMatching = {
  agreement: 'agreement',
  sign: 'signing',
  note: 'notes',
  view: 'view',
  edit: 'edit', // не проверено
  // chat: 'chat', // работает неккоретктно
  // timeline: 'timeline',
  // versions: 'versions', // не проверено
  // transcription: 'transcription', // ??
};

export const rightTabOfRS = {
  signing: 'file_setting.signing',
  agreement: 'file_setting.agreement'
}

const DetailsComponent = () => {  
  const [versionsData, setVersionsData] = React.useState<IDocument[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = React.useState(false);

  const { docsSegment, selectedKeys, isOpenRenameEntityModal, isOpenDeleteEntityModal,
    isDeletingDocuments, isOpenShareEntityModal, messageApi } = useAppSelector((state) => state.windowState);
  const { selectedDocument } = useAppSelector((state) => state.documents);
  const { userData } = useAppSelector((state) => state.userData);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { documentUrl, isDocumentLoading, isDocumentError } = useAppSelector(state => state.viewer);
  const { dataUserTable, } = useAppSelector((store) => store.dataUsers);
  const { isOpenAddNoteModal, notes } = useAppSelector((state) => state.notes);
  const { transcription } = useAppSelector((state) => state.aiFunctions);
  const { roomAllChats, chatData } = useAppSelector((state) => state.chat);
  const documentLogs = useAppSelector((state) => state.logs.document);
      
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en'
  const localizationText = locale === 'es' ? 'en' : locale;
  const params = useParams();
  const chatId = params?.chatId || localStorage.getItem('previousChatId');
  const possibleUseCollaboraAsViewer = isAvailableEditing === 'true' && [...convertableTypesFiles, 'pdf'].includes(selectedDocument?.extension!);
  const isExperimentalAvailable = userData?.is_experimental_available;

  let documentNameWithVersions: string;
  if (isOpenDeleteEntityModal) {
    const targetDoc = selectedDocument as IDocument;
    const versionsCnt = (targetDoc?.version_count && targetDoc?.version_count > 1)
      ? `(+${targetDoc?.version_count - 1} ${t('Documents.management.versionsCnt')})`
      : '';
    documentNameWithVersions = String(selectedDocument?.name) + versionsCnt;
  };

  React.useEffect(() => {
    if (dataRoom && !dataUserTable[0] && selectedDocument) {
      dispatch(fetchGroupsAndUsers({room_id: dataRoom?.id}));
    };
    return () => {
      dispatch(setDocsSegment('view'));
      dispatch(resetViewerSlice());
      dispatch(setSelectedNote(''));
      dispatch(setNotes(null));
      dispatch(setEntityOfContextMenu(null));
      dispatch(setDocumentsLogs([]));
      setVersionsData([]);
      dispatch(setTranscription(null));
      dispatch(setTranscriptionText(''));
      dispatch(setSessionStatus(null));
      dispatch(resetAgreementState());
      dispatch(resetSigningState());
      dispatch(setIsOpenAnalysisSider({ open: false, width: 0 }));
      dispatch(setIsOpenAIAssistanceSider({ open: false, width: 0 }));
      dispatch(setIsRSidebarCollapsed({
        collapsed: true,
        entity: '',
        width: 0
      }));
      dispatch(setSelectedVersion(null));

      localStorage.removeItem('document_id');
      localStorage.removeItem('modalSection');
      localStorage.removeItem('webviewer_file_id');
    }
  }, []);

  const fetchChatAndDoc = async (chat: IChat) => {
    if (!chat?.deleted_at) {
      const document = await api.getDocumentById(String(chat!.entity_id));
      dispatch(setSelectedDocument(document.data));
    } else {
      dispatch(setSelectedDocument({name: chat.title, deleted_at: chat?.deleted_at} as IDocument));
    };
    dispatch(fetchSetSelectedChat(chat));
    if (!chatData[chat.id] || !chatData[chat.id].isLoadedHistory)
      await dispatch(fetchChatHistory({ chat_id: chat.id, room_id: chat?.room_id!, page: 1 }));
    dispatch(setMessageForReply(null));
    dispatch(setIsUploadingSomeChat(false));
  };
  
  React.useEffect(() => {
    if (chatId && !selectedDocument) {
      dispatch(setViewType('Document'));
      dispatch(setDocsSegment('chat'));
      dispatch(setIsUploadingSomeChat(true));
    };
  }, []);
  
  React.useEffect(() => {
    if (chatId && !selectedDocument) {
      const targetChat = roomAllChats.find(chat => chat.id === chatId);
      if (targetChat) fetchChatAndDoc(targetChat);
    };
  }, [roomAllChats]);
  
  const docActions = React.useMemo(
    () => ({
      view: () => console.log('View section is open'),//!documentUrl && getCurrentFileToViewer(),
      notes:() => !notes && dispatch(fetchAllNotes(selectedDocument!)),  
      timeline:() => !documentLogs.logs[0] && dispatch(fetchAvailableDocumentsLogs({ document_id: selectedDocument!.id, locale: localizationText, versions: selectedDocument!.version_count })),
      versions: () => !versionsData[0] && getAllVersions(),
      chat: () => onClickChat(),
      edit: () => console.log('Editing section is open'),
      transcription: () => isExperimentalAvailable && !transcription && dispatch(checkTranscription({document: selectedDocument!, t})),
    }),
    [selectedDocument, documentLogs.logs, notes, versionsData, locale, documentUrl, transcription]
  );
  
  React.useEffect(() => {
    const action = docActions[docsSegment as keyof typeof docActions];
    action?.();
  },[docsSegment]);

  React.useEffect(() => {
    docsSegment === 'timeline' && dispatch(fetchAvailableDocumentsLogs({ document_id: selectedDocument!.id, locale: localizationText, versions: selectedDocument!.version_count }));
  },[locale]);

  React.useEffect(() => {
    dispatch(setEntityOfContextMenu(selectedDocument));
    setVersionsData([]);
    dispatch(setTranscription(null));
    dispatch(setTranscriptionText('')); 
    !possibleUseCollaboraAsViewer && getCurrentFileToViewer();
  },[selectedDocument?.id]);

  const onClickChat = async () => {
    if(!selectedDocument || selectedDocument?.status === 0 || selectedDocument?.deleted_at) return;
    dispatch(setIsUploadingSomeChat(true));
    const chatInfo = await dispatch(
      createChat({
        data: {
          entity_type: 0,
          entity_id: selectedDocument?.id,
          members_ids: dataRoom?.available_users?.map(({ id }) => id)!,
          title: selectedDocument?.name || '',
          room_id: dataRoom?.id!,
        },
        ownerId: userData?.id!,
      })
    );

    dispatch(setIsUploadingSomeChat(false));
    dispatch(fetchSetSelectedChat((chatInfo.payload as any).chat));
  };

  const getCurrentFileToViewer = async () => {    
    const abortController = new AbortController();
    dispatch(setViewerAbortController(abortController))

    selectedDocument && await dispatch(fetchDocumentToViewer({
      token: selectedDocument.token,
      extension: selectedDocument.extension,
      is_agreement: false,
      id: dataRoom?.id,
      entity: selectedDocument.id,
      action: selectedDocument.is_confidential ? 'view_confident': 'view',
      locale: i18n.language,
      name: selectedDocument.name,
      signal: abortController.signal,
    }));
  };

  const getAllVersions = async () => {
    setIsLoadingVersions(true);
    try {
      const versions = await api.getDocumentVersions(selectedDocument!.id);
              
      const preparedVersions = versions.data && versions.data[0] &&
        versions.data?.map((version: IDocument, i) => ({
          ...version,
          upload_at: dayjs(version.upload_at).valueOf(),
          author: version.author ? version.author.name : '',
          key: version.id,
          version_num: version.version_num ? Number(version.version_num) : i,
        }))
        .sort((a,b) => {
          if(a.id === selectedDocument!.id) return -1;
          if(b.id === selectedDocument!.id) return 1
          return b.version_num - a.version_num
        });

      setVersionsData(preparedVersions);
    } catch (e) {
      messageApi?.error(t('Documents.details.versions.errorGettingVersions'));
    } finally {
      setIsLoadingVersions(false)
    };
  };

  const correctViewer = (
    possibleUseCollaboraAsViewer
      ? <EditTab />
      : <WebViewer
        errorMessage={isDocumentError}
        isLoading={isDocumentLoading}
        url={documentUrl}
        getFile={getCurrentFileToViewer}
      />
  );

  const docsSegments = {
    view: correctViewer,
    notes: <NotesTab />,
    versions: <VersionsTab versionsData={versionsData} isLoadingVersions={isLoadingVersions} />,
    timeline: (
      <LogsTable
        logs={documentLogs.logs}
        scroll={{ y: window.innerHeight - 143, x: 800 }}
        isLoading={documentLogs.laoding}
        virtual={true}
      />
    ),
    chat: <Chat embedded={true}/>,
    edit: <EditTab />,
    loader: <></>,
    transcription: isExperimentalAvailable && <TranscriptionTab />,
  };

  const deleteSubmit = () => {
    const message = (text: string) => {
      return <div>
        <span>{documnentIcons[getCorrectIcon(selectedDocument?.extension || 'default')]}</span> {selectedDocument?.name} - {text}
      </div>
    };

    dispatch(deleteTargetEntity({
      entity: selectedDocument!,
      t,
      navigate: () => navigate(`/room/${dataRoom!.id}/chat`),
      message: message,
    }));
  }

  return <div style={{ width: '100%' }}>
    {docsSegments[docsSegment as keyof typeof docsSegments]}
    {isOpenAddNoteModal && (
      <AddNoteModal
        isOpen={isOpenAddNoteModal}
        onCancel={() => dispatch(setIsOpenAddNoteModal(false))}
        documentId={selectedDocument?.id!}
        documentName={selectedDocument?.name!}
      />
    )}

    {selectedKeys[0] !== 'documents' && (
      <React.Suspense fallback={<div />}>
        {isOpenRenameEntityModal && (
          <RenameModal
            open={isOpenRenameEntityModal}
            onCancel={() => dispatch(setIsOpenRenameEntityModal(false))}
            entity={selectedDocument!}
            type={'Document'}
          />
        )}
      </React.Suspense>
    )}

    {selectedKeys[0] !== 'documents' && (
      <React.Suspense fallback={<div />}>
        <ConfirmationModal
          isOpen={isOpenDeleteEntityModal}
          onCancel={() => dispatch(setIsOpenDeleteEntityModal(false))}
          isLoading={isDeletingDocuments}
          submit={deleteSubmit}
          text={`${`${t('Documents.modals.delete.confirmFile')}${documentNameWithVersions!}`}?`}
          title={`${t('Documents.modals.delete.deletion')} ${t('Documents.modals.delete.file')}`}
        />
      </React.Suspense>
    )}

    {selectedKeys[0] !== 'documents' && (
      <React.Suspense fallback={<div />}>
        {isOpenShareEntityModal && (
          <ShareModal
            open={isOpenShareEntityModal}
            onCancel={() => dispatch(setIsOpenShareEntityModal(false))}
            entity={selectedDocument!}
          />
        )}
      </React.Suspense>
    )}
  </div>
}

export default DetailsComponent;
